import {match} from 'ts-pattern';

import {always, filter, groupBy, indexBy, isNil, map, pipe, sort, toPairs} from 'ramda';

import {
  DefinitionWithTemplatesResponseBody,
  DocumentGroupEnum,
  DocumentGroupResponseBody,
  GetDefinitionTemplateListApiResponse,
  GetDocumentGroupListApiResponse,
  TemplateResponseBody,
} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {CATEGORY_BY_DOCUMENT_GROUP} from '../constans/CATEGORY_BY_DOCUMENT_GROUP';
import {TEMPLATE_CATEGORIES} from '../constans/TEMPLATE_CATEGORIES';
import {Category} from '../types/Category';

export const useGetTemplateGroupStructure = (
  definitionTemplateList: GetDefinitionTemplateListApiResponse | undefined,
  documentGroupList: GetDocumentGroupListApiResponse | undefined,
  authorizationProfiles: BaseAuthorizationProfile[] | undefined
) => {
  const [hasSalesLicense, canReadPneuhotel, canReadArticle, canReadServiceCase] = usePermissions({
    permissionKeys: ['businessCaseRead', 'pneuhotelRead', 'articleRead', 'serviceCaseRead'],
  });

  const groupsById = indexBy((group) => group.key, documentGroupList?.groups ?? []);

  const isDocumentGroupEnabled = (definition: DefinitionWithTemplatesResponseBody) => {
    if (definition.documentCode === 'gdpr-marketing-consent') {
      return false;
    }

    return match(definition.group)
      .with('warehouse', () => canReadArticle)
      .with('workshop', () => canReadServiceCase)
      .with('pneu_hotel', () => canReadPneuhotel)
      .with('business_case', () => hasSalesLicense)
      .with('vehicle_warehouse', () => hasSalesLicense)
      .otherwise(always(true));
  };

  const getTemplateGroupStructure = () => {
    if (isNil(definitionTemplateList)) {
      return [];
    }

    const primaryTemplatesIds = new Set<string>();
    definitionTemplateList.definitions.forEach((definition) => {
      primaryTemplatesIds.add(definition.primaryTemplateId);

      definition.authorizationProfiles.forEach(({primaryTemplateId}) =>
        primaryTemplatesIds.add(primaryTemplateId)
      );
    });

    const definitions = pipe(
      filter(isDocumentGroupEnabled),
      groupBy(groupDefinitionByCategory),
      toPairs,
      map(([categoryId, definitions]) => ({
        category: TEMPLATE_CATEGORIES[categoryId],
        groups: pipe(
          groupBy(groupByGroupId),
          toPairs,
          map(addGroupToDefinitions(groupsById)),
          map((item) => ({
            group: item.group,
            definitions: item.definitions.map((definition) => ({
              ...definition,
              authorizationProfiles: [
                {
                  authorizationProfile: {
                    authorizationProfileId: null,
                    title: i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'),
                    branchId: null,
                  },
                  templates: definition.templates
                    .filter((item) => isNil(item.authorizationProfileId))
                    .map(addIsPrimaryAttribute(primaryTemplatesIds))
                    .sort(sortByIsPrimary),
                },
                ...(authorizationProfiles
                  ?.filter((profile) => profile.isActive)
                  .map((profile) => ({
                    authorizationProfile: {
                      authorizationProfileId: profile.id,
                      title: profile.name,
                      branchId: profile.branchId,
                    },
                    templates: pipe(
                      filter(filterAuthProfiles(profile)),
                      map(addIsPrimaryAttribute(primaryTemplatesIds)),
                      sort(sortByIsPrimary)
                    )(definition.templates),
                  })) ?? []),
              ],
            })),
          }))
        )(definitions ?? []),
      })),
      sort(sortByPosition)
    )(definitionTemplateList.definitions);

    return definitions;
  };

  return [getTemplateGroupStructure];
};

const groupDefinitionByCategory = (definition: DefinitionWithTemplatesResponseBody) =>
  // should never be "unknown-category", this is only to prevent crash when new category is added on BE
  CATEGORY_BY_DOCUMENT_GROUP[definition.group]?.id ?? 'unknown-category';

const groupByGroupId = (definition: DefinitionWithTemplatesResponseBody) =>
  definition.group as string;

const sortByPosition = <A extends {category: Category}, B extends {category: Category}>(
  a: A,
  b: B
) => a.category.position - b.category.position;

const sortByIsPrimary = <A extends {isPrimary: boolean}, B extends {isPrimary: boolean}>(
  a: A,
  b: B
) => {
  const aPrimary = a.isPrimary ? 1 : 0;
  const bPrimary = b.isPrimary ? 1 : 0;

  return bPrimary - aPrimary;
};

const addGroupToDefinitions =
  (groupsById: Record<DocumentGroupEnum, DocumentGroupResponseBody>) =>
  ([group, definitions]: [string, DefinitionWithTemplatesResponseBody[] | undefined]) => ({
    group: {
      id: group,
      title: groupsById[group as DocumentGroupEnum]?.title ?? '',
    },
    definitions: definitions ?? [],
  });

const filterAuthProfiles =
  (profile: BaseAuthorizationProfile) => (template: TemplateResponseBody) =>
    template.authorizationProfileId === profile.id;

const addIsPrimaryAttribute =
  (primaryTemplatesIds: Set<string>) => (template: TemplateResponseBody) => ({
    ...template,
    isPrimary: primaryTemplatesIds.has(template.id),
  });
